/**
 * P2g (2026-07-05) — Global i18n Framework.
 *
 * Cel: każdy user ma swój język (PL/TH/RU) zapisany w DB i cała aplikacja
 * renderuje się w tym języku poprzez hook `useT()`.
 *
 * Architektura:
 *  1. Backend `users.language` + endpoint PATCH /users/me/language
 *  2. Auth zwraca `language` w /auth/me → AuthProvider ustawia w user object
 *  3. LanguageProvider ładuje odpowiedni słownik i wystawia `useT()` hook
 *  4. Fallback: gdy brak klucza w wybranym języku → PL, gdy brak w PL → key
 *
 * Zakres tłumaczeń FAZA 1 (ta sesja):
 *  - Sidebar nav labels
 *  - Login page
 *  - Dashboard (owner/manager/employee/warehouse)
 *  - Modały wylogowania, wspólne przyciski
 *
 * FAZA 2 (kolejne sesje):
 *  - Wydatki, Sejf, Rozliczenia, Vouchery, Alerty, Zamówienia, Katalog produkcji…
 *
 * Klucze niezdefiniowane w słowniku wracają nazwą klucza (dla łatwego debugu).
 */
import React, { createContext, useContext, useMemo, useCallback } from "react";
import { useAuth } from "@/lib/auth-context";
import { api } from "@/lib/api";
import { toast } from "sonner";

// ─────────────── SŁOWNIKI ───────────────
// Klucz płaski (np. "nav.dashboard") — ułatwia debug.
export const TRANSLATIONS = {
  pl: {
    // Wspólne
    "common.save": "Zapisz",
    "common.cancel": "Anuluj",
    "common.close": "Zamknij",
    "common.confirm": "Potwierdź",
    "common.delete": "Usuń",
    "common.edit": "Edytuj",
    "common.loading": "Ładowanie…",
    "common.error": "Błąd",
    "common.success": "Sukces",
    "common.today": "Dziś",
    "common.language": "Język",
    "common.logout": "Wyloguj",

    // Login
    "login.title": "Zaloguj się",
    "login.email": "Email",
    "login.password": "Hasło",
    "login.submit": "Zaloguj",
    "login.forgot": "Zapomniałem hasła",
    "login.remember": "Zapamiętaj mnie",

    // Sidebar / navigation
    "nav.dashboard": "Dashboard",
    "nav.orders": "Zamówienia",
    "nav.kitchen": "Kuchnia (zamówienie)",
    "nav.expenses": "Wydatki",
    "nav.safes": "Sejf / Bank",
    "nav.daily": "Rozliczenia dzienne",
    "nav.vouchers": "Vouchery",
    "nav.alerts": "Alerty",
    "nav.users": "Użytkownicy i uprawnienia",
    "nav.settings": "Ustawienia (progi legacy)",
    "nav.reconSettings": "Ustawienia rozliczeń",
    "nav.voucherSettings": "Ustawienia voucherów",
    "nav.reports": "Raporty",
    "nav.production": "Produkcja",
    "nav.productionReport": "Raport produkcji",
    "nav.checklists": "Checklisty zmian",
    "nav.products": "Produkty",
    "nav.categories": "Kategorie",
    "nav.dataQuality": "Jakość danych",
    "nav.suppliers": "Dostawcy",
    "nav.audit": "Audit log",
    "nav.feedback": "Zgłoszenia od pracowników",
    "sidebar.section.main": "Główne",
    "sidebar.section.daily": "Operacje dzienne",
    "sidebar.section.supply": "Zaopatrzenie",
    "sidebar.section.risk": "Ryzyko i kontrola",
    "sidebar.section.admin": "Raporty i administracja",

    // Dashboards (wspólne)
    "dash.hello": "Cześć",
    "dash.today": "Dziś",
    "dash.department": "Twój dział",

    // Language selector
    "lang.pl": "Polski",
    "lang.th": "ไทย (tajski)",
    "lang.ru": "Русский (rosyjski)",
    "lang.change": "Zmień język",
    "lang.saved": "Język zapisany",
  },

  th: {
    "common.save": "บันทึก",
    "common.cancel": "ยกเลิก",
    "common.close": "ปิด",
    "common.confirm": "ยืนยัน",
    "common.delete": "ลบ",
    "common.edit": "แก้ไข",
    "common.loading": "กำลังโหลด…",
    "common.error": "ข้อผิดพลาด",
    "common.success": "สำเร็จ",
    "common.today": "วันนี้",
    "common.language": "ภาษา",
    "common.logout": "ออกจากระบบ",

    "login.title": "เข้าสู่ระบบ",
    "login.email": "อีเมล",
    "login.password": "รหัสผ่าน",
    "login.submit": "เข้าสู่ระบบ",
    "login.forgot": "ลืมรหัสผ่าน",
    "login.remember": "จดจำฉัน",

    "nav.dashboard": "แดชบอร์ด",
    "nav.orders": "คำสั่งซื้อ",
    "nav.kitchen": "ครัว (สั่งซื้อ)",
    "nav.expenses": "ค่าใช้จ่าย",
    "nav.safes": "ตู้เซฟ / ธนาคาร",
    "nav.daily": "ปิดยอดประจำวัน",
    "nav.vouchers": "วอเชอร์",
    "nav.alerts": "การแจ้งเตือน",
    "nav.users": "ผู้ใช้และสิทธิ์",
    "nav.settings": "ตั้งค่า (เกณฑ์เดิม)",
    "nav.reconSettings": "ตั้งค่าการกระทบยอด",
    "nav.voucherSettings": "ตั้งค่าวอเชอร์",
    "nav.reports": "รายงาน",
    "nav.production": "การผลิต",
    "nav.productionReport": "รายงานการผลิต",
    "nav.checklists": "เช็คลิสต์กะ",
    "nav.products": "สินค้า",
    "nav.categories": "หมวดหมู่",
    "nav.dataQuality": "คุณภาพข้อมูล",
    "nav.suppliers": "ผู้จัดจำหน่าย",
    "nav.audit": "บันทึกการตรวจสอบ",
    "nav.feedback": "ข้อเสนอแนะจากพนักงาน",
    "sidebar.section.main": "หลัก",
    "sidebar.section.daily": "การปฏิบัติงานประจำวัน",
    "sidebar.section.supply": "การจัดหา",
    "sidebar.section.risk": "ความเสี่ยงและการควบคุม",
    "sidebar.section.admin": "รายงานและการบริหาร",

    "dash.hello": "สวัสดี",
    "dash.today": "วันนี้",
    "dash.department": "แผนกของคุณ",

    "lang.pl": "Polski (โปแลนด์)",
    "lang.th": "ไทย",
    "lang.ru": "Русский (รัสเซีย)",
    "lang.change": "เปลี่ยนภาษา",
    "lang.saved": "บันทึกภาษาแล้ว",
  },

  ru: {
    "common.save": "Сохранить",
    "common.cancel": "Отмена",
    "common.close": "Закрыть",
    "common.confirm": "Подтвердить",
    "common.delete": "Удалить",
    "common.edit": "Изменить",
    "common.loading": "Загрузка…",
    "common.error": "Ошибка",
    "common.success": "Готово",
    "common.today": "Сегодня",
    "common.language": "Язык",
    "common.logout": "Выход",

    "login.title": "Вход в систему",
    "login.email": "Электронная почта",
    "login.password": "Пароль",
    "login.submit": "Войти",
    "login.forgot": "Забыли пароль",
    "login.remember": "Запомнить меня",

    "nav.dashboard": "Панель",
    "nav.orders": "Заказы",
    "nav.kitchen": "Кухня (заказ)",
    "nav.expenses": "Расходы",
    "nav.safes": "Сейф / Банк",
    "nav.daily": "Итоги дня",
    "nav.vouchers": "Ваучеры",
    "nav.alerts": "Оповещения",
    "nav.users": "Пользователи и права",
    "nav.settings": "Настройки (старые пороги)",
    "nav.reconSettings": "Настройки сверки",
    "nav.voucherSettings": "Настройки ваучеров",
    "nav.reports": "Отчёты",
    "nav.production": "Производство",
    "nav.productionReport": "Отчёт производства",
    "nav.checklists": "Чек-листы смен",
    "nav.products": "Товары",
    "nav.categories": "Категории",
    "nav.dataQuality": "Качество данных",
    "nav.suppliers": "Поставщики",
    "nav.audit": "Журнал аудита",
    "nav.feedback": "Обратная связь сотрудников",
    "sidebar.section.main": "Главное",
    "sidebar.section.daily": "Ежедневные операции",
    "sidebar.section.supply": "Снабжение",
    "sidebar.section.risk": "Риски и контроль",
    "sidebar.section.admin": "Отчёты и администрирование",

    "dash.hello": "Привет",
    "dash.today": "Сегодня",
    "dash.department": "Ваш отдел",

    "lang.pl": "Polski (польский)",
    "lang.th": "ไทย (тайский)",
    "lang.ru": "Русский",
    "lang.change": "Сменить язык",
    "lang.saved": "Язык сохранён",
  },
};

const LanguageContext = createContext({ lang: "pl", t: (k) => k, setLang: () => {} });

export function LanguageProvider({ children }) {
  const { user, refresh } = useAuth();
  // Fallback do PL gdy user nie zalogowany / bez języka
  const lang = user?.language && ["pl", "th", "ru"].includes(user.language) ? user.language : "pl";

  const t = useCallback(
    (key, fallback) => {
      const dict = TRANSLATIONS[lang] || TRANSLATIONS.pl;
      if (dict[key]) return dict[key];
      if (TRANSLATIONS.pl[key]) return TRANSLATIONS.pl[key];
      // Ostatnia deska ratunku: fallback string albo klucz (widoczne w dev jako TODO).
      return fallback ?? key;
    },
    [lang]
  );

  const setLang = useCallback(
    async (newLang) => {
      if (!["pl", "th", "ru"].includes(newLang)) return;
      try {
        await api.patch("/users/me/language", { language: newLang });
        toast.success(TRANSLATIONS[newLang]["lang.saved"] || "OK");
        // Odśwież profil użytkownika żeby AuthProvider zaktualizował user.language
        if (typeof refresh === "function") await refresh();
      } catch (e) {
        toast.error("Nie udało się zmienić języka");
      }
    },
    [refresh]
  );

  const value = useMemo(() => ({ lang, t, setLang }), [lang, t, setLang]);
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

/**
 * Hook: `const { t, lang, setLang } = useT();`
 * Użycie: `<h1>{t("dash.hello")}, {user.name}!</h1>`
 */
export function useT() {
  return useContext(LanguageContext);
}
